/* eslint-disable consistent-return, react/no-danger, react/jsx-props-no-spreading */
import classNames from 'classnames';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React, { useCallback, useState, useMemo } from 'react';
import { FormattedDate } from 'react-intl';
import { useLocation } from 'react-router';

import { useDateGroups } from '../../hooks/useDates';
import * as AppPropTypes from '../../lib/PropTypes';

import { useItemRefs } from '../../contexts/ItemRefContext';
import Header from '../headers/Header';
import SectionList from '../lists/SectionList';
import StickyMenu from '../menus/StickyMenu';

import styles from '../../../styles/pages/section.module.scss';

const propTypes = {
    page: AppPropTypes.page.isRequired,
    className: PropTypes.string,
};

const defaultProps = {
    className: null,
};

const SectionPage = ({ page, className }) => {
    const {
        pages: items,
        listType = 'bundles',
        title = null,
        shortTitle = null,
        description = null,
        edition = null,
        url = null,
        dates = null,
        summary = null,
        slug = null,
    } = page || {};

    const { hash = null } = useLocation();
    const [initialHash] = useState(hash !== null ? hash.substring(1) : null);
    const { scrollToRef, currentItem } = useItemRefs();

    const parsedDatesArray = useMemo(() => {
        if (dates === null) {
            return;
        }
        const datesArray =
            dates !== null ? Object.values(dates).filter((date) => date !== null) : [];

        return datesArray.map((value) => {
            const parsedDate = dayjs(value, 'YYYY-MM-DD');
            const id = parsedDate.format('YYYY-MM-DD');
            const { id: currentId, type: currentType } = currentItem || {};
            return {
                label: (
                    <div className={styles.itemContent}>
                        <span className={styles.day}>
                            <FormattedDate value={parsedDate} weekday="short" />
                        </span>
                        <span className={styles.dayNumber}>
                            <FormattedDate value={parsedDate} day="numeric" />
                        </span>
                    </div>
                ),
                value,
                id,
                startDate: value,
                active: currentId === id && currentType === 'cardsList',
            };
        });
    }, [dates, currentItem]);

    const finalDatesArray = useDateGroups(parsedDatesArray, 'YYYY-MM', { month: 'long' });

    const bundlesArray = useMemo(() => {
        const { id: currentId, type: currentType } = currentItem || {};
        return listType === 'bundles' && items !== null && items.data !== null
            ? items.data.map((it) => ({
                  ...it,
                  active: currentId === it.id && currentType === 'cardsList',
              }))
            : null;
    }, [listType, items, currentItem]);

    const onClickPill = useCallback(
        (item) => {
            // console.log('yo', item);
            scrollToRef({ ...item, type: 'cardsList' }, 120, true);
        },
        [scrollToRef],
    );

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
        >
            <Header
                type="section"
                edition={edition}
                slug={slug}
                title={shortTitle || title}
                summary={summary}
                description={description}
                listType={listType}
            />
            {(listType === 'bundles' && bundlesArray.length > 0) ||
            (listType === 'events' && finalDatesArray.length > 0) ? (
                <StickyMenu
                    groups={
                        listType === 'events' && finalDatesArray !== null
                            ? finalDatesArray
                            : [{ items: bundlesArray }]
                    }
                    pillClassName={styles.pillButton}
                    onClickPill={onClickPill}
                    isDark
                />
            ) : null}
            <div className={styles.main}>
                <SectionList
                    className={styles.filmList}
                    items={items}
                    title={title}
                    description={description}
                    listType={listType}
                    url={url}
                    initialHash={initialHash}
                    edition={edition}
                    withHeader={items.length > 1}
                    dates={parsedDatesArray}
                    isDark
                />
            </div>
        </div>
    );
};

SectionPage.defaultProps = defaultProps;
SectionPage.propTypes = propTypes;

export default SectionPage;
